import { useEffect } from "react";
import type { AbstractAgent } from "@ag-ui/client";
import { useSWRConfig } from "swr";
import { FEATURED_SESSION_SHARE_KEY } from "~/pages/manifold-nextgen/components/projects/useFeaturedSessionAgents";
import { projectConversationsPath } from "~/services/projects";

/**
 * Re-validate a project's session lists when an agent run ends, so the sidebar's
 * "Your Sessions" rail, the sessions tab, and the featured rail reflect the run
 * without a manual page refresh. Both the sidebar and the sessions tab read the
 * same SWR keys, so revalidating by key updates every surface at once.
 *
 * Two independent reads go stale when a run adds a turn (server-side writes SWR
 * can't observe):
 *
 * - The project's session list (`projectConversationsPath`) carries each
 *   session's `agent_ids`, which the conversations service computes from its
 *   turns. A run by a new agent grows that set — and the live-session rows
 *   render an avatar stack off it — so the list is revalidated on every run end.
 * - The featured rail resolves each entry's served agents live from its share
 *   read, cached under `[FEATURED_SESSION_SHARE_KEY, token]`. Those served sets
 *   also grow as agents join, so every mounted share read is revalidated by key
 *   prefix. The rail is curated and small and the reads are summary-only, so the
 *   fan-out is cheap; a run in a non-featured session simply refetches unchanged
 *   sets. The rail's own list (which sessions are pinned) is untouched — a run
 *   never pins or unpins — so it is not revalidated here.
 *
 * Keyed off the terminal run event, matching `useProjectDatasetSync`: only the
 * run's end guarantees the turn's agent has landed.
 */
export function useProjectSessionsSync(agent: AbstractAgent, projectId: string | undefined) {
  const { mutate } = useSWRConfig();
  useEffect(() => {
    if (!projectId) return;
    const revalidate = () => {
      void mutate(projectConversationsPath(projectId));
      void mutate((key) => Array.isArray(key) && key[0] === FEATURED_SESSION_SHARE_KEY);
    };
    const sub = agent.subscribe({
      onRunFinishedEvent: revalidate,
      onRunErrorEvent: revalidate,
    });
    return () => sub.unsubscribe();
  }, [agent, projectId, mutate]);
}
